// Swaps known Shelfology phone links to source-specific tracking numbers.
(() => {
  'use strict';

  const STORAGE_KEY = 'hd_tracking_source';
  const ATTRIBUTION_FIELDS = Object.freeze(['gclid', 'fbclid', 'utm_source', 'utm_medium', 'utm_campaign']);
  const TRACKING_NUMBERS = Object.freeze({
    google_ads:     Object.freeze({ display: '213.491.1200', tel: '+12134911200' }),
    google_organic: Object.freeze({ display: '606.217.0021', tel: '+16062170021' }),
    meta:           Object.freeze({ display: '440.367.0399', tel: '+14403670399' }),
    meta_organic:   Object.freeze({ display: '573.802.1100', tel: '+15738021100' }),
    direct:         Object.freeze({ display: '949.244.1083', tel: '+19492441083' }),
    klaviyo:        Object.freeze({ display: '505.520.3344', tel: '+15055203344' }),
    default:        Object.freeze({ display: '949.244.1083', tel: '+19492441083' })
  });
  const SHELFOLOGY_REAL_NUMBER = '9492441083';
  const US_PHONE_PATTERN = /(^|[^\d+])((?:\+?1[\s.-]*)?(?:\(\s*\d{3}\s*\)|\d{3})[\s.-]*\d{3}[\s.-]*\d{4})(?!\d)/g;
  const KNOWN_PHONE_DIGITS = new Set([
    ...Object.values(TRACKING_NUMBERS).map(({ tel }) => normalizePhone(tel)),
    SHELFOLOGY_REAL_NUMBER
  ]);

  function normalizePhone(value) {
    const digits = String(value || '').replace(/\D/g, '');
    return digits.length === 11 && digits.charAt(0) === '1' ? digits.slice(1) : digits;
  }

  function replaceKnownPhoneValue(value, displayNumber) {
    return String(value || '').replace(US_PHONE_PATTERN, (match, prefix, phoneNumber) => (
      KNOWN_PHONE_DIGITS.has(normalizePhone(phoneNumber)) ? `${prefix}${displayNumber}` : match
    ));
  }

  function getSearchParams() {
    try {
      return new URLSearchParams(window.location.search);
    } catch (error) {
      return { get: () => '' };
    }
  }

  function storageGet(key) {
    try {
      return window.sessionStorage.getItem(key) || '';
    } catch (error) {
      return '';
    }
  }

  function storageSet(key, value) {
    try {
      window.sessionStorage.setItem(key, value);
    } catch (error) {
      // Storage can be blocked by browser privacy settings.
    }
  }

  function storageRemove(key) {
    try {
      window.sessionStorage.removeItem(key);
    } catch (error) {
      // Storage can be blocked by browser privacy settings.
    }
  }

  function clearStoredAttribution() {
    storageRemove(STORAGE_KEY);
    ATTRIBUTION_FIELDS.forEach((field) => storageRemove(`hd_${field}`));
  }

  function storeAttributionParams(params) {
    ATTRIBUTION_FIELDS.forEach((field) => {
      const value = String(params.get(field) || '').trim();
      if (!value) return;

      storageSet(`hd_${field}`, value);
    });
  }

  function hasMarketingConsent() {
    const customerPrivacy = window.Shopify && window.Shopify.customerPrivacy;

    if (!customerPrivacy) return true;

    try {
      if (typeof customerPrivacy.marketingAllowed === 'function') {
        return customerPrivacy.marketingAllowed();
      }

      if (typeof customerPrivacy.userCanBeTracked === 'function') {
        return customerPrivacy.userCanBeTracked();
      }

      if (typeof customerPrivacy.getTrackingConsent === 'function') {
        const trackingConsent = customerPrivacy.getTrackingConsent();

        if (trackingConsent === 'yes') return true;
        if (trackingConsent === 'no' || trackingConsent === 'no_interaction') return false;
      }
    } catch (error) {
      return true;
    }

    return true;
  }

  function getHostname(value) {
    try {
      return new URL(value).hostname.toLowerCase().replace(/^www\./, '');
    } catch (error) {
      return '';
    }
  }

  function hostMatches(hostname, domain) {
    return hostname === domain || hostname.endsWith(`.${domain}`);
  }

  function getSourceFromUtm(utmSource, utmMedium) {
    const source = String(utmSource || '').toLowerCase();
    const medium = String(utmMedium || '').toLowerCase();

    if (/(^|[^a-z0-9])(google|adwords|googleads)([^a-z0-9]|$)/.test(source)) {
      return /(^|[^a-z0-9])(cpc|ppc|paid)([^a-z0-9]|$)/.test(medium) ? 'google_ads' : 'google_organic';
    }
    if (/(^|[^a-z0-9])(meta|facebook|instagram|fb|ig)([^a-z0-9]|$)/.test(source)) {
      return /(^|[^a-z0-9])(cpc|ppc|paid|paidsocial)([^a-z0-9]|$)/.test(medium) ? 'meta' : 'meta_organic';
    }
    if (
      /(^|[^a-z0-9])(klaviyo|email|newsletter)([^a-z0-9]|$)/.test(source) ||
      /(^|[^a-z0-9])(email|newsletter)([^a-z0-9]|$)/.test(medium)
    ) return 'klaviyo';

    return '';
  }

  function getSourceFromReferrer() {
    const hostname = getHostname(document.referrer);

    if (hostMatches(hostname, 'google.com')) return 'google_organic';
    if (hostMatches(hostname, 'facebook.com') || hostMatches(hostname, 'instagram.com')) return 'meta_organic';

    return '';
  }

  function getCurrentTrafficSource(params) {
    const clickSource = params.get('gclid') ? 'google_ads' : params.get('fbclid') ? 'meta' : '';
    return clickSource || getSourceFromUtm(params.get('utm_source'), params.get('utm_medium'));
  }

  function getStoredTrafficSource() {
    if (storageGet('hd_gclid')) return 'google_ads';
    if (storageGet('hd_fbclid')) return 'meta';

    const storedUtmSource = storageGet('hd_utm_source');
    const storedUtmMedium = storageGet('hd_utm_medium');
    const storedUtmTrafficSource = getSourceFromUtm(storedUtmSource, storedUtmMedium);
    const storedSource = storageGet(STORAGE_KEY);

    return storedUtmTrafficSource || (TRACKING_NUMBERS[storedSource] ? storedSource : '');
  }

  function getTrafficSource(params, canUseStoredAttribution) {
    return (
      getCurrentTrafficSource(params) ||
      (canUseStoredAttribution ? getStoredTrafficSource() : '') ||
      getSourceFromReferrer() ||
      'direct'
    );
  }

  function isKnownPhoneLink(anchor) {
    const href = anchor.getAttribute('href') || '';

    if (!/^tel:/i.test(href)) return false;

    return KNOWN_PHONE_DIGITS.has(normalizePhone(href));
  }

  function replaceKnownPhoneText(anchor, displayNumber) {
    const textNodes = [];
    const walker = document.createTreeWalker(anchor, NodeFilter.SHOW_TEXT);
    let node = walker.nextNode();

    while (node) {
      textNodes.push(node);
      node = walker.nextNode();
    }

    textNodes.forEach((textNode) => {
      textNode.nodeValue = replaceKnownPhoneValue(textNode.nodeValue, displayNumber);
    });
  }

  function replaceKnownPhoneAttribute(anchor, attribute, displayNumber) {
    const value = anchor.getAttribute(attribute);
    if (!value) return;

    anchor.setAttribute(attribute, replaceKnownPhoneValue(value, displayNumber));
  }

  function isPhoneOnlyText(value) {
    let hasKnownPhone = false;

    return String(value || '')
      .replace(US_PHONE_PATTERN, (match, prefix, phoneNumber) => {
        const isKnownPhone = KNOWN_PHONE_DIGITS.has(normalizePhone(phoneNumber));
        hasKnownPhone = hasKnownPhone || isKnownPhone;

        return isKnownPhone ? prefix : match;
      })
      .replace(/[^A-Za-z0-9]+/g, '')
      .trim() === '' && hasKnownPhone;
  }

  function syncAccessibleName(anchor, displayNumber) {
    const ariaLabel = anchor.getAttribute('aria-label');

    if (ariaLabel) {
      replaceKnownPhoneAttribute(anchor, 'aria-label', displayNumber);
      return;
    }

    const visibleText = anchor.textContent.replace(/\s+/g, ' ').trim();

    if (!visibleText) {
      anchor.setAttribute('aria-label', `Call Shelfology at ${displayNumber}`);
    } else if (isPhoneOnlyText(visibleText)) {
      anchor.setAttribute('aria-label', `${displayNumber}, call Shelfology`);
    }
  }

  function swapPhoneLinks(numbers) {
    document.querySelectorAll('a[href]').forEach((anchor) => {
      if (!isKnownPhoneLink(anchor)) return;

      anchor.setAttribute('href', `tel:${numbers.tel}`);
      
      replaceKnownPhoneText(anchor, numbers.display);
      syncAccessibleName(anchor, numbers.display);
      replaceKnownPhoneAttribute(anchor, 'title', numbers.display);
    });
  }

  function applyPhoneNumbers() {
    const params = getSearchParams();
    const canStoreAttribution = hasMarketingConsent();
    const source = getTrafficSource(params, canStoreAttribution);
    const numbers = TRACKING_NUMBERS[source] || TRACKING_NUMBERS.default;

    if (canStoreAttribution) {
      storeAttributionParams(params);
      if (source !== 'direct') storageSet(STORAGE_KEY, source);
    } else {
      clearStoredAttribution();
    }

    swapPhoneLinks(numbers);
    sendUtmToWebhook(numbers.tel);
  }

  function swapImmediatePhoneNumbers() {
    const params = getSearchParams();
    const source = getTrafficSource(params, false);
    const numbers = TRACKING_NUMBERS[source] || TRACKING_NUMBERS.default;

    swapPhoneLinks(numbers);
  }

  function sendUtmToWebhook(toNumber) {
    try {
      var payload = {
        to_number:    toNumber,
        utm_source:   storageGet('hd_utm_source'),
        utm_medium:   storageGet('hd_utm_medium'),
        utm_campaign: storageGet('hd_utm_campaign'),
        gclid:        storageGet('hd_gclid'),
        fbclid:       storageGet('hd_fbclid')
      };
      navigator.sendBeacon(
        '/apps/hermes-dial/store-utm',
        new Blob([JSON.stringify(payload)], { type: 'application/json' })
      );
    } catch (e) {}
  }

  function loadCustomerPrivacyApi(callback) {
    if (window.Shopify && window.Shopify.customerPrivacy) {
      callback();
      return;
    }

    if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') {
      callback();
      return;
    }

    window.Shopify.loadFeatures([
      {
        name: 'consent-tracking-api',
        version: '0.1',
        onLoad: () => {
          callback();
        }
      }
    ]);
  }

  function init() {
    swapImmediatePhoneNumbers();
    loadCustomerPrivacyApi(applyPhoneNumbers);
    document.addEventListener('visitorConsentCollected', applyPhoneNumbers);
    document.addEventListener('trackingConsentAccepted', applyPhoneNumbers);
    document.addEventListener('trackingConsentDeclined', applyPhoneNumbers);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();